// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { dealerSelectors, initActionTypes } from '@makemydeal/dr-shared-store';
import type { StateTree } from '@makemydeal/dr-dash-types';

// consts/enums
import {
    ADD_CATALOG_ACCESSORIES,
    ADDED_MANUAL_ACCESSORIES,
    ADD_ACCESSORIES_SELECTIONS,
    REMOVE_CATALOG_ACCESSORIES,
    ACCESSORIES_OPEN_FORM
} from '../actionTypes/accessoriesActionTypes';
import { DASH_START_ACCESSORIES_CATALOG } from '../actionTypes/navigationActionTypes';

// actions
import {
    addAccessoriesSelections,
    checkAccessoriesCatalogExists,
    completeAccessoriesProgress,
    startAccessoriesProgress,
    useAccessoriesInitSuccess
} from '../actions/accessoriesActionCreators';

// interfaces/types
import type { DashMiddleware, DashStore, DashNext } from '@makemydeal/dr-shared-store';

// utils
import * as accessoriesSelectors from '../selectors/accessoriesSelectors';
import * as offerReduxSelectors from '../selectors/offerRedux';
import * as vehicleSelectors from '../selectors/vehicleSelectors';

import * as accessoriesActionCreators from '../actions/accessoriesActionCreators';
import type { AddCatalogAccessoriesActionPayload, RemoveCatalogAccessoriesActionPayload } from '../types/types';
import { toSelectedAccessory } from '../utils/manualAccessoriesUtils';
import { cloneDeep } from 'lodash';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

const getAccessoriesData = (state: StateTree) => ({
    hasCatalogAccessories: accessoriesSelectors.getHasCatalogAccessories(state),
    isAccessoriesCatalogEnabled: dealerSelectors.isAccessoriesCatalogEnabled(state),
    currentAccessories: accessoriesSelectors.getAccessoriesList(state),
    manualAccessories: dealerSelectors.getManualAccessories(state)
});

const getDealData = (state: StateTree) => ({
    offerType: offerReduxSelectors.getCurrentOfferType(state),
    dealXgId: offerReduxSelectors.getDealXgId(state),
    dealXgVersion: offerReduxSelectors.getDealXgVersion(state),
    dealerId: dealerSelectors.getDealerId(state),
    vin: vehicleSelectors.getVehicleVin(state)
});

const getAccessoriesAvailableOptions = (
    hasCatalogAccessories: string | boolean,
    isAccessoriesCatalogEnabled: string | boolean,
    manualAccessories: any
) => {
    const options = [];
    if (hasCatalogAccessories && isAccessoriesCatalogEnabled) {
        options.push('catalog');
    }
    if (manualAccessories && manualAccessories.length > 0) {
        options.push('picklist');
    }
    options.push('manual_entry');
    return options;
};

const logNewRelicAccessoriesInit = (state: StateTree) => {
    const { hasCatalogAccessories, isAccessoriesCatalogEnabled, manualAccessories } = getAccessoriesData(state);
    const dealData = getDealData(state);

    let initialState = 'none';
    if (hasCatalogAccessories && isAccessoriesCatalogEnabled) {
        initialState = 'catalog_available';
    } else if (manualAccessories && manualAccessories.length > 0) {
        initialState = 'picklist_available';
    }

    logNewRelicPageAction('MV:Accessories - Init', {
        ...dealData,
        hasManualAccessories: Boolean(manualAccessories && manualAccessories.length > 0),
        isAccessoriesCatalogEnabled,
        hasCatalogAccessories,
        initialState,
        pickListItemsCount: manualAccessories?.length || 0
    });
};

const logNewRelicAccessoriesSectionOpened = (state: StateTree, actionType: string) => {
    const { hasCatalogAccessories, isAccessoriesCatalogEnabled, currentAccessories, manualAccessories } = getAccessoriesData(state);
    const dealData = getDealData(state);

    const availableOptions = getAccessoriesAvailableOptions(hasCatalogAccessories, isAccessoriesCatalogEnabled, manualAccessories);

    let hasPickList = false;
    let pickListItemsCount = 0;
    if (manualAccessories) {
        hasPickList = manualAccessories.length > 0;
        pickListItemsCount = manualAccessories.length;
    }

    let currentAccessoriesCount = 0;
    if (currentAccessories) {
        currentAccessoriesCount = currentAccessories.length;
    }

    logNewRelicPageAction('MV:Accessories - Section Opened', {
        ...dealData,
        availableOptions: availableOptions.join(','),
        hasCatalogAccessories,
        hasPickList,
        pickListItemsCount,
        currentAccessoriesCount,
        actionType
    });
};

const logNewRelicAccessoriesItemsSaved = (state: StateTree, accessories: any[], skipPayment: boolean) => {
    const dealData = getDealData(state);

    const catalogCount = accessories.filter((acc: any) => !acc.isManualAccessory).length;
    const manualCount = accessories.filter((acc: any) => acc.isManualAccessory).length;
    const totalCount = accessories.length;

    logNewRelicPageAction('MV:Accessories - Items Saved', {
        ...dealData,
        totalAccessories: totalCount,
        catalogAccessories: catalogCount,
        manualAccessories: manualCount,
        skipPayment
    });

    accessories.forEach((acc: any) => {
        logNewRelicPageAction('MV:Accessories - Item Detail', {
            dealerId: dealData.dealerId,
            dealXgId: dealData.dealXgId,
            accessoryName: acc.name,
            accessoryCode: acc.code,
            isManualEntry: acc.isManualAccessory,
            amount: acc.amount
        });
    });
};

export const middleware: DashMiddleware = (store: DashStore) => (next: DashNext) => (action: AnyFSA) => {
    next(action);
    const state = store.getState();

    switch (action.type) {
        case initActionTypes.INIT_PENCIL_SUCCESS: {
            store.dispatch(useAccessoriesInitSuccess(action.payload.accessories));
            if (!vehicleSelectors.getIsQuoteWithoutVehicle(state)) {
                store.dispatch(checkAccessoriesCatalogExists());
            }
            logNewRelicAccessoriesInit(state);
            break;
        }
        case DASH_START_ACCESSORIES_CATALOG:
        case ACCESSORIES_OPEN_FORM: {
            store.dispatch(startAccessoriesProgress());
            logNewRelicAccessoriesSectionOpened(state, action.type);
            break;
        }
        case ADDED_MANUAL_ACCESSORIES: {
            store.dispatch(addAccessoriesSelections(action.meta?.skipPayment));

            const accessories = action.payload?.manualAccessories?.accessories || [];
            logNewRelicAccessoriesItemsSaved(state, accessories, action.meta?.skipPayment || false);
            break;
        }
        case ADD_ACCESSORIES_SELECTIONS: {
            store.dispatch(completeAccessoriesProgress());

            break;
        }

        case ADD_CATALOG_ACCESSORIES: {
            const { accessories: catalogAccessories } = action.payload as AddCatalogAccessoriesActionPayload;
            const accessories = cloneDeep(accessoriesSelectors.getAccessoriesList(state));

            catalogAccessories.map(toSelectedAccessory).forEach((newAccessory) => {
                const existingAccessoryIndex = accessories.findIndex((accessory) => accessory.code === newAccessory.code);

                if (existingAccessoryIndex >= 0) {
                    Object.assign(accessories[existingAccessoryIndex], newAccessory);
                } else {
                    accessories.push(newAccessory);
                }
            });

            store.dispatch(accessoriesActionCreators.addManualAccessories(accessories));

            break;
        }
        case REMOVE_CATALOG_ACCESSORIES: {
            const { accessories: catalogAccessories } = action.payload as RemoveCatalogAccessoriesActionPayload;
            const accessories = cloneDeep(accessoriesSelectors.getAccessoriesList(state));

            const hasBeenChanged = catalogAccessories.reduce((hasBeenChanged, catalogAccessory) => {
                const existingAccessoryIndex = accessories.findIndex(
                    (accessory) =>
                        catalogAccessory.part_number === accessory.manualAccessoryId ||
                        catalogAccessory.part_number === accessory.code
                );

                if (existingAccessoryIndex >= 0) {
                    accessories.splice(existingAccessoryIndex, 1);

                    return true;
                }

                return hasBeenChanged;
            }, false);

            if (hasBeenChanged) {
                store.dispatch(accessoriesActionCreators.addManualAccessories(accessories, action.meta));
            }

            break;
        }
    }
};
